import React, { PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import { Slide, SlideProps } from '@mui/material';
import { Flex } from '../Flex';
import { Box } from '../Box';
import { Snackbar, SnackbarProps } from './Snackbar';

type SnackbarContextValue = {
  pushSnackbar: (props: SnackbarPublicProps) => string;
  updateSnackbar: (id: string, props: SnackbarPublicProps) => string;
};

const generateSnackbarId = () => Math.random().toString(36).substr(2, 5);

const PUSH_SNACKBAR = 'PUSH_SNACKBAR';
const UPDATE_SNACKBAR = 'UPDATE_SNACKBAR';
const REMOVE_SNACKBAR = 'REMOVE_SNACKBAR';
const START_EXIT_SNACKBAR = 'START_EXIT_SNACKBAR';

type ReducerAction<T, V> = {
  type: T;
  payload: V;
};

type PushSnackbarAction = ReducerAction<
  typeof PUSH_SNACKBAR,
  {
    id: string;
    props: SnackbarPublicProps;
  }
>;

type UpdateSnackbarAction = ReducerAction<
  typeof UPDATE_SNACKBAR,
  {
    id: string;
    props: SnackbarPublicProps;
  }
>;

type RemoveSnackbarAction = ReducerAction<
  typeof REMOVE_SNACKBAR,
  {
    id: string;
  }
>;

type StartExitSnackbarAction = ReducerAction<
  typeof START_EXIT_SNACKBAR,
  {
    id: string;
  }
>;

export type SnackbarPublicProps = Omit<SnackbarProps, 'destroy'> & { as?: React.ElementType } & {
  customProps?: Record<string, any> | null;
};
type SnackbarStateShape = SnackbarPublicProps & { id: string; exiting?: boolean };
type SnackbarStateAction =
  | PushSnackbarAction
  | UpdateSnackbarAction
  | RemoveSnackbarAction
  | StartExitSnackbarAction;

const snackbarStateReducer = (snackbars: SnackbarStateShape[], action: SnackbarStateAction) => {
  switch (action.type) {
    case PUSH_SNACKBAR:
      return [...snackbars, { id: action.payload.id, ...action.payload.props }];
    case UPDATE_SNACKBAR:
      return snackbars.map(snackbar =>
        snackbar.id === action.payload.id ? { ...snackbar, ...action.payload.props } : snackbar,
      );
    case START_EXIT_SNACKBAR:
      return snackbars.map(snackbar =>
        snackbar.id === action.payload.id ? { ...snackbar, exiting: true } : snackbar,
      );
    case REMOVE_SNACKBAR:
      return snackbars.filter(s => s.id !== action.payload.id);
    default:
      return snackbars;
  }
};

const SnackbarContext = React.createContext<SnackbarContextValue>({
  pushSnackbar: () => '',
  updateSnackbar: () => '',
});

const positionStyles: Record<NonNullable<SnackbarProps['position']>, Record<string, unknown>> = {
  'top-left': { top: 0, left: 24 },
  'top-middle': { top: 0, left: '50%', transform: 'translate(-50%, 0)' },
  'top-right': { top: 0, right: 24 },
  'bottom-left': { bottom: 12, left: 24 },
  'bottom-middle': { bottom: 12, left: '50%', transform: 'translate(-50%, 0)' },
  'bottom-right': { bottom: 12, right: 24 },
};

/**
 * A component that acts both as a state-manager and provider. It provides access to methods for
 * managing snackbar instances
 */
export const SnackbarProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [snackbars, dispatch] = React.useReducer<
    React.Reducer<SnackbarStateShape[], SnackbarStateAction>
  >(snackbarStateReducer, []);

  const pushSnackbar = React.useCallback(
    (props: SnackbarPublicProps) => {
      const id = generateSnackbarId();
      dispatch({ type: PUSH_SNACKBAR, payload: { id, props } });
      return id;
    },
    [dispatch],
  );

  const updateSnackbar = React.useCallback(
    (id: string, props: SnackbarPublicProps) => {
      dispatch({ type: UPDATE_SNACKBAR, payload: { id, props } });
      return id;
    },
    [dispatch],
  );

  const removeSnackbar = React.useCallback(
    (id: SnackbarStateShape['id']) => {
      // First mark as exiting to trigger animation
      dispatch({ type: START_EXIT_SNACKBAR, payload: { id } });
      // Then remove after animation completes (MUI Slide default is 225ms)
      setTimeout(() => {
        dispatch({ type: REMOVE_SNACKBAR, payload: { id } });
      }, 500);
      return id;
    },
    [dispatch],
  );

  // Determine slide direction based on position
  const getSlideDirection = (
    position: NonNullable<SnackbarProps['position']>,
  ): SlideProps['direction'] => {
    if (position.includes('left')) {
      return 'right';
    }
    if (position.includes('right')) {
      return 'left';
    }
    if (position.includes('top')) {
      return 'down';
    }
    return 'up';
  };

  const renderSnackbars = () => {
    if (typeof window === 'undefined') {
      return null;
    }

    const listOfPositions = Object.keys(positionStyles) as (keyof typeof positionStyles)[];
    return createPortal(
      <>
        {listOfPositions.map(position => {
          // Filter snackbars for this position
          const snackbarsForPosition = snackbars.filter(snackbar =>
            snackbar.position ? snackbar.position === position : position === 'bottom-left',
          );

          if (snackbarsForPosition.length === 0) {
            return null;
          }

          const slideDirection = getSlideDirection(position);

          return (
            <Flex
              key={position}
              data-testid={`${position}-snackbar-wrapper`}
              position="fixed"
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-end"
              zIndex={9999}
              sx={positionStyles[position]}
            >
              {snackbarsForPosition.map(({ id, exiting, ...snackbarPublicProps }) => (
                <Slide
                  key={id}
                  direction={slideDirection}
                  in={!exiting}
                  timeout={500}
                  mountOnEnter
                  unmountOnExit
                >
                  <Box pt={3} width="100%" maxWidth="500px">
                    <Snackbar destroy={() => removeSnackbar(id)} {...snackbarPublicProps} />
                  </Box>
                </Slide>
              ))}
            </Flex>
          );
        })}
      </>,
      document.body,
    );
  };

  const contextPayload = React.useMemo(
    () => ({ pushSnackbar, updateSnackbar, removeSnackbar }),
    [pushSnackbar, updateSnackbar, removeSnackbar],
  );

  return (
    <SnackbarContext.Provider value={contextPayload}>
      {renderSnackbars()}
      {children}
    </SnackbarContext.Provider>
  );
};

/** A shortcut for the consumer component */
export const SnackbarConsumer = SnackbarContext.Consumer;

/** An alternative to the consumer component through hooks */
export const useSnackbar = () => React.useContext(SnackbarContext);
